import { yupResolver } from '@hookform/resolvers/yup';
import {
  ProductPurchaseModel,
  ProductPurchaseMutationInput,
} from 'api-hooks/product/order';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import {
  CardSection,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  ProductPurchaseFormSchema,
  ProductPurchaseFormType,
} from './form-type';
import InformationField from './information-field';
import OrderDetailField from './order-detail-field';
import OtherFeesField from './other-fees';
import ProductDetailField from './product-detail-field.tsx';
import ProductPurchaseSelectInput from './select-input';
import { FormContent, FormDivider } from './styles';
import BottomSection from './summary-price/bottom-section';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  productPurchase?: ProductPurchaseModel;
  onSubmit: (
    input: ProductPurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ProductPurchaseModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: ProductPurchaseFormType;
}

export default function ProductOrderForm(props: Props) {
  const { productPurchase } = props;
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    productPurchase,
    defaultValues: props.defaultValues,
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(ProductPurchaseFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: ProductPurchaseFormType) => {
      try {
        const {
          agentStatus,
          productPurchase,
          tempTransactionAt,
          remainingTransactionQuota,
          isExceedingQuota,
          ...rest
        } = values;

        const productPurchases = rest.productPurchases.map((pp) => ({
          ...pp,
          productVariantId: pp.productVariantId.id,
        }));

        const needApproval =
          values.transactionAt.getTime() !== tempTransactionAt.getTime();

        const result = {
          ...rest,
          productPurchases,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('product-purchases');
  const { onVoid, voidMutate, isActive, voidPermission } = useVoidAction(
    'product-purchases',
    productPurchase,
  );
  const { requestApprovalButton } = useApprovalAction(
    'product-purchases',
    productPurchase,
  );
  const { resolverButton } = useResolveApproval(
    'product-purchases',
    productPurchase,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'product-purchases',
    productPurchase,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'product-purchases',
    productPurchase,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (productPurchase) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
          {revertVoidButton}
        </div>
      );
    }
    return undefined;
  }, [
    productPurchase,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: ProductPurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!productPurchase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        productPurchase &&
        `${t('common:product_purchase')} - ${productPurchase.number}`
      }
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={productPurchase}
              noSubmit
              onDelete={onVoid}
              deleteText={t('common:void')}
              isDeleteLoading={voidMutate.isLoading}
              noDelete={editable || !voidPermission}
              noEdit={!isActive || !permission.update}
              noCancel={editable}
              title={
                productPurchase ? (
                  <TitleStatus
                    status={productPurchase.status}
                    title={`${t('common:product_purchase')} - ${
                      productPurchase.number
                    }`}
                    type="purchase"
                  />
                ) : (
                  t('common:add_extra', {
                    extra: t('common:product_purchase'),
                  })
                )
              }
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        {props.renderError}
        <div>
          {productPurchase &&
            (!!productPurchase.parentPurchase ||
              !!(productPurchase.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={productPurchase}
                navigation="ProductPurchasesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <ProductPurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </SectionContainer>
          <InformationField />
          <OrderDetailField productPurchase={productPurchase} />
          <SeparatorVertical $margin={64} />
          <CardSection>
            <ProductDetailField />
            <SeparatorVertical $margin={24} />
            <OtherFeesField />
          </CardSection>
          <SeparatorVertical $margin={36} />
          <OtherPriceComponent
            type="product-purchases"
            data={props.productPurchase}
          />

          <FormDivider />
        </div>
        <BottomSection productPurchase={productPurchase} />
      </FormContent>
    </Form>
  );
}
